/*  CWL DJ Mixer browser demo: the main thread.

    window.CwlMixer: one mixer on one loop.

      const mixer = new CwlMixer ();          // URLs resolve against this script's own URL
      await mixer.loadMeta ();                 // loop.json: the loop's name, tempo and length. Small.
      await mixer.play ();                     // the first call loads the Wasm, the worklet and the loop
      mixer.stop ();
      mixer.setParam ('eq_low_gain_db', -26);  // a value in the parameter's units, by Parameters.cpp ID
      mixer.setParamNormalised ('channel_filter_amount', 0.25);   // a knob position, 0 to 1
      mixer.setBpm (128);
      await mixer.loadLoop (arrayBuffer, { name, bpm });
      await mixer.loadUserFile (file);         // an <input type="file"> pick; tempo stays at loop.json's
      mixer.tap ();                            // tap tempo: the mean of the last four taps' intervals

    Nothing is fetched until play() (or loadUserFile()) asks for it, so the page paints first. The
    reverb bank for BOG THROW is fetched on the first ensureBank() (web/js, job 2).

    The loop runs on two alternating AudioBufferSourceNodes, each started on an exact frame, one
    loop length after the last, rather than on the loop flag: the worklet's quarter note is anchored
    to the first frame and runs on, so the tempo clock is sample accurate across every seam.

    A classic script, not a module: Shopify's asset pipeline leaves it alone.
*/

(function (global)
{
    'use strict';

    const SCRIPT_URL = (document.currentScript && document.currentScript.src) || global.location.href;

    const DEFAULT_URLS =
    {
        wasm:       'cwl-mixer.wasm',
        processor:  'cwl-mixer-processor.js',
        loop:       'loop.wav',
        loopJson:   'loop.json',
        bank:       'bog-bank.json'
    };

    const LOOKAHEAD_S = 0.12;        // how far ahead of now the first loop is started
    const SCHEDULE_EVERY_MS = 200;   // how often the next loop pass is checked for

    class CwlMixer
    {
        constructor (options)
        {
            options = options || {};
            this.base = options.baseUrl || SCRIPT_URL;
            this.urls = Object.assign ({}, DEFAULT_URLS, options.urls || {});
            this.preferredRate = options.sampleRate || 48000;

            this.ctx = null;
            this.node = null;
            this.ids = null;              // parameter IDs in the module's index order
            this.loading = null;
            this.ready = false;

            this.params = new Map();      // id -> { norm: bool, v }: replayed into a fresh worklet
            this.colorOff = false;

            this.meta = null;
            this.bpm = 120;
            this.loopName = '';
            this.buffer = null;
            this.defaultBpm = 120;

            this.playing = false;
            this.startFrame = 0;
            this.nextIndex = 0;
            this.sources = [];
            this.timer = null;

            this.taps = [];
            this.pending = new Map();     // telemetry requests in flight
            this.bankPromise = null;      // the reverb bank's fetch, once asked for
            this.bankBytes = null;
            this.bankReady = false;
            this.heldType = null;         // BOG THROW, chosen before its bank was in

            this.handlers = { ready: [], state: [], error: [], bank: [] };
        }

        url (name) { return new URL (this.urls[name], this.base).href; }

        on (event, fn) { (this.handlers[event] = this.handlers[event] || []).push (fn); return this; }
        emit (event, data) { for (const fn of this.handlers[event] || []) { try { fn (data); } catch (e) { console.error (e); } } }

        state ()
        {
            return { playing: this.playing, ready: this.ready, loading: !! this.loading && ! this.ready,
                     bpm: this.bpm, loopName: this.loopName, sampleRate: this.ctx ? this.ctx.sampleRate : 0 };
        }

        // ---- loading ---------------------------------------------------------------------

        async loadMeta ()
        {
            if (this.meta) return this.meta;
            const r = await fetch (this.url ('loopJson'));
            if (! r.ok) throw new Error ('loop.json: ' + r.status);
            this.meta = await r.json();
            this.defaultBpm = this.meta.bpm;
            if (! this.buffer) { this.bpm = this.meta.bpm; this.loopName = this.meta.name || this.meta.source || this.meta.file; }
            this.emit ('state', this.state());
            return this.meta;
        }

        /** The AudioContext, made on a user gesture. 48 kHz where the browser allows it. */
        context ()
        {
            if (this.ctx) return this.ctx;
            const AC = global.AudioContext || global.webkitAudioContext;
            try { this.ctx = new AC ({ sampleRate: this.preferredRate, latencyHint: 'interactive' }); }
            catch (e) { this.ctx = new AC ({ latencyHint: 'interactive' }); }
            return this.ctx;
        }

        load ()
        {
            if (this.loading) return this.loading;

            this.loading = (async () =>
            {
                const ctx = this.context();
                this.emit ('state', this.state());

                // The loop is the long download: it starts now, alongside the engine.
                const loopBytes = this.buffer ? null : this.fetchWithProgress (this.url ('loop'), 'loop');
                if (loopBytes) loopBytes.catch (() => {});

                const [wasmBytes] = await Promise.all ([
                    fetch (this.url ('wasm')).then ((r) => { if (! r.ok) throw new Error ('cwl-mixer.wasm: ' + r.status); return r.arrayBuffer(); }),
                    ctx.audioWorklet.addModule (this.url ('processor')),
                    this.loadMeta()
                ]);

                this.node = new AudioWorkletNode (ctx, 'cwl-mixer',
                {
                    numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2],
                    channelCount: 2, channelCountMode: 'explicit', channelInterpretation: 'speakers',
                    processorOptions: { wasmBytes }
                });
                this.node.connect (ctx.destination);
                this.node.onprocessorerror = (e) => { this.processorError = String (e && (e.message || e.type)); this.emit ('error', new Error ('the audio engine stopped: ' + this.processorError)); };

                const ready = new Promise ((resolve, reject) =>
                {
                    this.node.port.onmessage = (e) =>
                    {
                        const msg = e.data;
                        if (msg.type === 'ready') resolve (msg);
                        else if (msg.type === 'error') reject (new Error (msg.message));
                        else this.onWorkletMessage (msg);
                    };
                });

                const info = await ready;
                this.node.port.onmessage = (e) => this.onWorkletMessage (e.data);
                this.ids = info.ids;
                this.latency = info.latency;

                // Everything the panel set before the engine existed, and the bank if it came first.
                for (const [id, p] of this.params) this.post (id, p);
                if (this.bankBytes) await this.installBank();
                if (this.colorOff) this.node.port.postMessage ({ type: 'colorOff', off: true });
                this.node.port.postMessage ({ type: 'transport', anchorFrame: 0, anchorPpq: 0, bpm: this.bpm, playing: false });

                if (! this.buffer && loopBytes)
                    this.buffer = await ctx.decodeAudioData (await loopBytes);

                this.ready = true;
                this.emit ('ready', info);
                this.emit ('state', this.state());
            }) ().catch ((err) =>
            {
                this.loading = null;
                this.emit ('error', err);
                this.emit ('state', this.state());
                throw err;
            });

            return this.loading;
        }

        /** The loop is 16.5 MB: its download reports progress ('progress' events) so Play can say so. */
        async fetchWithProgress (url, what)
        {
            const r = await fetch (url);
            if (! r.ok) throw new Error (what + ': ' + r.status);
            const total = Number (r.headers.get ('content-length')) || 0;
            if (! r.body || ! total) return r.arrayBuffer();

            const out = new Uint8Array (total);
            const reader = r.body.getReader();
            let loaded = 0;
            for (;;)
            {
                const { done, value } = await reader.read();
                if (done) break;
                if (loaded + value.length > out.length) return (await new Response (new Blob ([out.subarray (0, loaded), value])).arrayBuffer());
                out.set (value, loaded);
                loaded += value.length;
                this.emit ('progress', { what, loaded, total });
            }
            return loaded === total ? out.buffer : out.slice (0, loaded).buffer;
        }

        onWorkletMessage (msg)
        {
            if (msg.type === 'telemetry')
            {
                const resolve = this.pending.get ('telemetry');
                this.pending.delete ('telemetry');
                if (resolve) resolve (msg);
            }
            else if (msg.type === 'bank')
            {
                const resolve = this.pending.get ('bank');
                this.pending.delete ('bank');
                if (resolve) resolve (msg.ok);
            }
        }

        // ---- parameters ------------------------------------------------------------------

        post (id, p)
        {
            /*  BOG THROW runs on the Breath Of God reverb's tables and heads, which are fetched the
                first time it is chosen. Until they are in, the engine keeps the type it had, so it is
                never run on BOG THROW without them; the panel shows it chosen and loading. */
            if (id === 'slot1_fx_type')
            {
                const type = p.norm ? Math.round (p.v * 9) : Math.round (p.v);
                if (type === 4 && ! this.bankReady) { this.heldType = p; this.ensureBank(); return; }
                this.heldType = null;
            }

            if (! this.node || ! this.ids) return;
            const index = this.ids.indexOf (id);
            if (index < 0) return;
            this.node.port.postMessage ({ type: p.norm ? 'norm' : 'value', index, value: p.v });
        }

        setParam (id, value)             { const p = { norm: false, v: value }; this.params.set (id, p); this.post (id, p); }
        setParamNormalised (id, value)   { const p = { norm: true, v: value };  this.params.set (id, p); this.post (id, p); }

        setColorFxOff (off)
        {
            this.colorOff = !! off;
            if (this.node) this.node.port.postMessage ({ type: 'colorOff', off: this.colorOff });
        }

        /** Meters, gain reduction, tempo and, if asked, the spectrum tap. Null before the engine loads. */
        telemetry (withSpectrum)
        {
            if (! this.node || ! this.ready || this.pending.has ('telemetry')) return Promise.resolve (null);
            return new Promise ((resolve) =>
            {
                // A reply that never comes (a worklet mid-reload) must not wedge the panel's meters.
                const timer = setTimeout (() => { if (this.pending.get ('telemetry') === done) { this.pending.delete ('telemetry'); resolve (null); } }, 500);
                const done = (msg) => { clearTimeout (timer); resolve (msg); };
                this.pending.set ('telemetry', done);
                this.node.port.postMessage ({ type: 'telemetry', spectrum: !! withSpectrum });
            });
        }

        // ---- the reverb bank (job 2) -------------------------------------------------------

        /** Fetches the bank once (bog-bank.json and the two files it names) and installs it. */
        ensureBank ()
        {
            if (this.bankPromise) return this.bankPromise;
            this.emit ('bank', 'loading');

            this.bankPromise = (async () =>
            {
                const manUrl = this.url ('bank');
                const man = await (await fetch (manUrl)).json();
                const get = async (file) => { const r = await fetch (new URL (file, manUrl).href); if (! r.ok) throw new Error (file + ': ' + r.status); return r.arrayBuffer(); };
                const [tables, heads] = await Promise.all ([get (man.tables.file), get (man.heads.file)]);
                this.bankBytes = { man, tables, heads };
                await this.installBank();
            }) ().catch ((err) =>
            {
                this.bankPromise = null;
                this.emit ('bank', 'error');
                this.emit ('error', err);
            });

            return this.bankPromise;
        }

        async installBank ()
        {
            if (this.bankReady || ! this.bankBytes || ! this.node || ! this.ids) return;
            const { man, tables, heads } = this.bankBytes;

            const ok = await new Promise ((resolve) =>
            {
                this.pending.set ('bank', resolve);
                this.node.port.postMessage ({ type: 'bank', tables, heads, headAnchors: man.heads.anchors,
                                              headFrames: man.heads.length, headRate: man.heads.rate }, [tables, heads]);
            });

            this.bankBytes = null;
            if (! ok) { this.emit ('bank', 'error'); this.emit ('error', new Error ('the reverb bank was refused')); return; }

            this.bankReady = true;
            this.emit ('bank', 'ready');
            if (this.heldType) { const p = this.heldType; this.heldType = null; this.post ('slot1_fx_type', p); }
        }

        // ---- the loop ---------------------------------------------------------------------

        async play ()
        {
            await this.load();
            const ctx = this.ctx;
            if (ctx.state !== 'running') await ctx.resume();
            if (this.playing) return;

            const sr = ctx.sampleRate;
            this.startFrame = Math.ceil ((ctx.currentTime + LOOKAHEAD_S) * sr);
            this.nextIndex = 0;
            this.playing = true;

            // The quarter note is 0 on the loop's first frame and runs on through every pass.
            this.node.port.postMessage ({ type: 'transport', anchorFrame: this.startFrame, anchorPpq: 0, bpm: this.bpm, playing: true });

            this.schedule();
            this.timer = setInterval (() => this.schedule(), SCHEDULE_EVERY_MS);
            this.emit ('state', this.state());
        }

        /** Keeps two passes in hand: the one playing and the next, each on its exact frame. */
        schedule ()
        {
            if (! this.playing) return;
            const ctx = this.ctx, sr = ctx.sampleRate, n = this.buffer.length;

            // Pass k starts on frame startFrame + k n: the frame the pass before it ends on.
            while (this.sources.length < 2)
            {
                const src = ctx.createBufferSource();
                src.buffer = this.buffer;
                src.connect (this.node);
                src.start ((this.startFrame + this.nextIndex * n) / sr);
                src.onended = () =>
                {
                    const i = this.sources.indexOf (src);
                    if (i >= 0) this.sources.splice (i, 1);
                    try { src.disconnect(); } catch (e) {}
                    this.schedule();
                };
                this.sources.push (src);
                ++this.nextIndex;
            }
        }

        stop ()
        {
            if (! this.playing) return;
            this.playing = false;
            clearInterval (this.timer);
            this.timer = null;
            for (const s of this.sources) { s.onended = null; try { s.stop(); s.disconnect(); } catch (e) {} }
            this.sources = [];
            // The effects' tails ring on: the worklet keeps running, fed silence.
            if (this.node) this.node.port.postMessage ({ type: 'transport', playing: false });
            this.emit ('state', this.state());
        }

        setBpm (bpm)
        {
            bpm = Math.max (20, Math.min (300, Number (bpm) || this.bpm));
            this.bpm = bpm;
            if (this.node) this.node.port.postMessage ({ type: 'transport', bpm });   // re-anchored in the worklet: no jump
            this.emit ('state', this.state());
        }

        /** Tap tempo: the mean interval of the last four taps. A pause of two seconds starts over. */
        tap ()
        {
            const now = performance.now();
            if (this.taps.length && now - this.taps[this.taps.length - 1] > 2000) this.taps = [];
            this.taps.push (now);
            if (this.taps.length > 4) this.taps.shift();
            if (this.taps.length < 2) return null;
            const mean = (this.taps[this.taps.length - 1] - this.taps[0]) / (this.taps.length - 1);
            const bpm = Math.round (60000 / mean * 10) / 10;
            this.setBpm (bpm);
            return bpm;
        }

        async loadLoop (arrayBuffer, info)
        {
            info = info || {};
            const ctx = this.context();
            const buffer = await ctx.decodeAudioData (arrayBuffer);
            const wasPlaying = this.playing;
            this.stop();
            this.buffer = buffer;
            this.loopName = info.name || 'Your track';
            this.setBpm (info.bpm || this.defaultBpm);
            this.emit ('state', this.state());
            if (wasPlaying) await this.play();
            return buffer;
        }

        async loadUserFile (file)
        {
            await this.loadMeta().catch (() => {});
            const name = (file.name || 'Your track').replace (/\.[^.]+$/, '');
            return this.loadLoop (await file.arrayBuffer(), { name, bpm: this.defaultBpm });
        }
    }

    global.CwlMixer = CwlMixer;
}) (window);
